
import Vue from "vue";
import AppAssessmentListingFilterHeader from "@/components/recruiter/app_assessment/AppAssessmentListingFilterHeader.vue";
import GoBackHeader from "@/components/shared/GoBackHeader.vue";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { mapActions, mapGetters, mapMutations } from "vuex";
import DeleteItem from "@/components/shared/DeleteItem.vue";
import {
  APP_ASSESSMENTS,
  SITE_DIRECTION
} from "@/store/modules/common/constants";
import { AssessmentListingHeaderOptions } from "@/interfaces/responses/assessments/assessments";
import {
  ACTIVE_ASSESSMENT,
  APP_ANNOTATION_ASSESSMENT_LISTING,
  RESET_ASSESSMENT
} from "@/store/modules/recruiter/constants";
import { SiteDirections } from "@/store/modules/common/interfaces";
import {
  format_date,
  get_app_assessment_status,
  get_app_assessment_status_color,
  get_color_for_percentage,
  get_pass_or_fail_class,
  assessment_pass_fail_result
} from "@/utils/global";
import { get_color_by_name } from "@/utils/colors";
import DataNotFound from "@/components/shared/DataNotFound.vue";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { AppAnnotationAssessmentListing } from "@/store/modules/recruiter/interfaces";
import { User } from "@/interfaces/data_objects/user";
import OnBoardingInvitationCard from "@/components/recruiter/OnBoardingInvitationCard.vue";
import {
  USER_DETAILS_BY_ID,
  GET_COMPANY_DETAILS
} from "@/store/modules/auth/constants";
import RefreshButton from "@/components/shared/RefreshButton.vue";
import { ANNOTATION_ASSESSMENT_SORT_CONFIG } from "@/utils/assessment_sort_config";

export default Vue.extend({
  name: "AppAnnotationAssessmentReport",
  components: {
    AppAssessmentListingFilterHeader,
    GoBackHeader,
    DataNotFound,
    DeleteItem,
    OnBoardingInvitationCard,
    RefreshButton
  },
  data() {
    return {
      loading: false, // Loading state of the page
      data_error: false, // Error state of the page
      assessment: null as AppAssessments.Assessment | null, // Assessment object
      table_headers: [
        {
          title: this.$t("assessments.listing.col-1").toString(),
          cols: 1,
          sortable: false,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-4").toString(),
          cols: 2,
          sortable: true,
          direction: "desc"
        },
        { title: this.$t("assessments.listing.col-2").toString(), cols: 2 },
        {
          title: this.$t("assessments.listing.col-score").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        { title: this.$t("assessments.listing.col-3").toString(), cols: 3 },
        {
          title: this.$t("assessments.listing.col-5").toString(),
          cols: 2,
          is_btn: true
        }
      ] as AssessmentListingHeaderOptions[],
      delete_icon: require("@/assets/icons/bold/reset.svg"),
      is_delete: false,
      selected_user: {
        id: 0,
        last_name: "",
        first_name: "",
        avatar_uri: ""
      } as AppAssessments.AssessmentUserObject,
      delete_btn_loading: false,
      version: "v1" as string,
      on_boarding_invitation: false,
      user_object: {} as User,
      status: null as AppAssessments.AppAssessmentStatus | null,
      sort_index: 3,
      sort_field_config: ANNOTATION_ASSESSMENT_SORT_CONFIG,
      selected_annotation_type: "" as string
    };
  },
  beforeRouteEnter(to, from, next) {
    const version = to.params.version;
    if (version === "v1" || version === "v2" || version === "v3") {
      next();
    } else {
      next({ name: "annotation-assessment-not-found" });
    }
  },
  beforeRouteUpdate(to, from, next) {
    const version = to.params.version;
    if (version === "v1" || version === "v2" || version === "v3") {
      next();
    } else {
      next({ name: "annotation-assessment-not-found" });
    }
  },
  created() {
    this.version = this.$route.params.version || "v1";
    // Get default annotation type from company settings
    const assessment_setting =
      this.get_company_details?.storage_path?.assessment_setting;
    const default_annotation_type =
      assessment_setting?.candidates_annotation || "annotation_v1";
    this.selected_annotation_type = default_annotation_type;
    // Update version based on default annotation type
    if (default_annotation_type === "annotation_v2") {
      this.version = "v2";
    } else if (default_annotation_type === "annotation_v3") {
      this.version = "v3";
    }
  },
  watch: {
    app_assessments: {
      handler(newVal: AppAssessments.Assessment[]) {
        // When app_assessments loads, ensure selected type exists
        if (newVal && newVal.length > 0 && this.selected_annotation_type) {
          const exists = newVal.some(
            (assessment: AppAssessments.Assessment) =>
              assessment.assessment_type === this.selected_annotation_type
          );
          // If selected type doesn't exist, use first available Annotation assessment
          if (!exists && !this.assessment) {
            const firstAnnotation = newVal.find(
              (assessment: AppAssessments.Assessment) =>
                assessment.assessment_type?.startsWith("annotation_")
            );
            if (firstAnnotation) {
              this.selected_annotation_type = firstAnnotation.assessment_type;
              // Update version based on selected type
              if (firstAnnotation.assessment_type === "annotation_v2") {
                this.version = "v2";
              } else if (firstAnnotation.assessment_type === "annotation_v3") {
                this.version = "v3";
              } else {
                this.version = "v1";
              }
              // Re-initialize if not already initialized
              if (!this.assessment) {
                this.init_process();
              }
            }
          }
        }
      },
      immediate: true
    },
    "CurrentAssessment.selected_dates.date_filter": function (
      new_value: string[],
      old_value: string[]
    ) {
      if (
        (new_value.length === 2 || new_value.length === 0) &&
        new_value.length !== old_value.length
      )
        this.process_assessment_listing(1);
    },
    "CurrentAssessment.selected_candidates": function (
      new_value: string[],
      old_value: string[]
    ) {
      if (JSON.stringify(new_value) !== JSON.stringify(old_value))
        this.process_assessment_listing(1);
    },
    get_site_direction() {
      this.table_headers = [
        {
          title: this.$t("assessments.listing.col-1").toString(),
          cols: 1,
          sortable: false,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-4").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-2").toString(),
          cols: 2
        },
        {
          title: this.$t("assessments.listing.col-score").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        { title: this.$t("assessments.listing.col-3").toString(), cols: 3 },
        { title: this.$t("assessments.listing.col-5").toString(), cols: 2 }
      ];
    }
  },
  computed: {
    SiteDirections() {
      return SiteDirections;
    },
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS,
      get_site_direction: SITE_DIRECTION
    }),
    ...mapGetters("recruiter", {
      app_annotation_assessment_listing: APP_ANNOTATION_ASSESSMENT_LISTING
    }),
    ...mapGetters("auth", {
      get_company_details: GET_COMPANY_DETAILS
    }),
    not_found_error() {
      return this.$t("errors.annotation-assessments-not-found").toString();
    },
    AppAssessmentsStatus() {
      return AppAssessments.AppAssessmentStatus;
    },
    CurrentAssessment(): AppAnnotationAssessmentListing {
      // Use server-side sorting like English assessment; no client sorting here
      return this.app_annotation_assessment_listing(this.version);
    },
    annotation_assessment_items() {
      // Dynamically generate items from app_assessments store (from database)
      return this.app_assessments
        .filter((assessment: AppAssessments.Assessment) =>
          assessment.assessment_type?.startsWith("annotation_")
        )
        .map((assessment: AppAssessments.Assessment) => {
          // Format the assessment name for display
          const type = assessment.assessment_type || "";
          const version = type.replace("annotation_", "").toUpperCase();
          return {
            title: `Annotation ${version}`,
            value: assessment.assessment_type
          };
        })
        .sort(
          (
            a: { title: string; value: string },
            b: { title: string; value: string }
          ) => {
            // Sort by version number (v1, v2, v3)
            const aVersion = a.value.replace("annotation_", "");
            const bVersion = b.value.replace("annotation_", "");
            return aVersion.localeCompare(bVersion);
          }
        );
    }
  },
  async mounted() {
    await this.init_process();
  },
  methods: {
    format_date,
    get_app_assessment_status,
    get_app_assessment_status_color,
    get_color_for_percentage,
    get_pass_or_fail_class,
    assessment_pass_fail_result,
    ...mapActions("recruiter", {
      fetch_annotation_assessment_listing: APP_ANNOTATION_ASSESSMENT_LISTING,
      delete_user_annotation_assessment: RESET_ASSESSMENT
    }),
    ...mapMutations("recruiter", {
      set_active_assessment: ACTIVE_ASSESSMENT
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION,
      set_root_error: ROOT_ERROR
    }),
    ...mapActions("auth", {
      fetch_user_by_id: USER_DETAILS_BY_ID
    }),
    async init_process() {
      this.loading = true;
      // Wait for app_assessments to be loaded if empty
      if (!this.app_assessments || this.app_assessments.length === 0) {
        this.loading = false;
        return;
      }
      // Find the assessment based on selected annotation type
      this.assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) =>
          val.assessment_type === this.selected_annotation_type
      );
      // If assessment is not found, try to find any annotation assessment
      if (!this.assessment) {
        this.assessment = this.app_assessments.find(
          (val: AppAssessments.Assessment) =>
            val.assessment_type?.startsWith("annotation_")
        );
        // Update selected type to match what's available
        if (this.assessment) {
          this.selected_annotation_type = this.assessment.assessment_type;
          // Update version based on found assessment
          if (this.assessment.assessment_type === "annotation_v2") {
            this.version = "v2";
          } else if (this.assessment.assessment_type === "annotation_v3") {
            this.version = "v3";
          } else {
            this.version = "v1";
          }
        }
      }
      // If no annotation assessment is found
      if (!this.assessment) {
        this.data_error = true;
        this.loading = false;
        return;
      }
      this.set_active_assessment({
        assessment_type: this.assessment.assessment_type,
        assessment_id: this.assessment.id
      });

      await this.fetch_assessment_by_pagination(
        this.CurrentAssessment.page,
        this.CurrentAssessment.limit,
        this.assessment.id,
        false // Don't fetch all annotations, fetch only selected type
      );
    },
    on_annotation_type_change() {
      // When annotation type changes, find the corresponding assessment and refetch
      this.assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) =>
          val.assessment_type === this.selected_annotation_type
      );
      if (this.assessment) {
        // Update version based on selected annotation type
        if (this.selected_annotation_type === "annotation_v1") {
          this.version = "v1";
        } else if (this.selected_annotation_type === "annotation_v2") {
          this.version = "v2";
        } else if (this.selected_annotation_type === "annotation_v3") {
          this.version = "v3";
        }
        this.set_active_assessment({
          assessment_type: this.assessment.assessment_type,
          assessment_id: this.assessment.id
        });
        // Reset to page 1 and refetch
        this.process_assessment_listing(1);
      }
    },
    async process_assessment_listing(page: number) {
      if (!this.assessment) return;
      await this.fetch_assessment_by_pagination(
        page,
        this.CurrentAssessment.limit,
        this.assessment.id,
        false // Don't fetch all annotations, fetch only selected type
      );
    },
    async fetch_assessment_by_pagination(
      page = 1,
      limit: number,
      assessment_id: number,
      fetch_all_annotations = false
    ) {
      this.loading = true;
      const res = await this.fetch_annotation_assessment_listing({
        page,
        limit,
        assessment_id,
        selected_dates: this.CurrentAssessment.selected_dates,
        selected_candidates: this.CurrentAssessment.selected_candidates,
        version: this.version,
        score_filter: this.CurrentAssessment.score_filter,
        status_filter: this.CurrentAssessment.status_filter,
        sort_by: this.CurrentAssessment.sort_by,
        sort_order: this.CurrentAssessment.sort_order,
        fetch_all_annotations
      });
      if (!res) {
        this.data_error = true;
        this.loading = false;
        return;
      }
      this.loading = false;
    },
    transform_view_icon(icon: HTMLElement): HTMLElement {
      const color = get_color_by_name("success");
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    view_assessment_result(assessment_result_id: number, user_id: number) {
      // Map annotation type to version for the route
      let route_version = "v1";
      if (this.selected_annotation_type === "annotation_v2") {
        route_version = "v2";
      } else if (this.selected_annotation_type === "annotation_v3") {
        route_version = "v3";
      }
      this.$router.push(
        `/recruiter/assessments/annotation/${route_version}/${assessment_result_id}/view/${user_id}`
      );
    },
    transform_trash_icon(icon: HTMLElement): HTMLElement {
      const color = "#FF4066";
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    delete_assessment(user: AppAssessments.AssessmentUserObject) {
      this.selected_user = user;
      this.delete_btn_loading = false;
      this.is_delete = true;
    },
    async dialog_action(value: boolean) {
      if (value) {
        this.delete_btn_loading = true;
        const response = await this.delete_user_annotation_assessment({
          assessment_id: this.assessment?.id,
          user_id: this.selected_user.id
        });
        if (response) {
          this.set_root_notification(
            `${this.selected_user.first_name} ${
              this.selected_user.last_name
            } ${this.$t("assessments.assessment-reset")}`
          );
          this.process_assessment_listing(1);
        } else {
          this.set_root_error(
            `${this.selected_user.first_name} ${
              this.selected_user.last_name
            } ${this.$t("assessments.assessment-reset-failed")}`
          );
        }
        this.is_delete = false;
      }
    },
    get_percentage(result: AppAssessments.AnnotationAssessmentResults) {
      return Object.values(AppAssessments.AnnotationAssessmentRefs).reduce(
        (acc, key) => {
          if (result && result.result) {
            const part = result.result[key];
            if (part) {
              acc += part.score;
            }
            return acc;
          }
          return 0;
        },
        0
      );
    },
    update_filter(filter: string) {
      this.CurrentAssessment.score_filter = filter;
      this.process_assessment_listing(1);
    },
    async sort_assessment(ind: number) {
      this.table_headers[ind].direction =
        this.table_headers[ind].direction === "asc" ? "desc" : "asc";

      const sortField = this.sort_field_config[ind];
      if (sortField && this.assessment?.id) {
        this.CurrentAssessment.sort_by = sortField;
        this.CurrentAssessment.sort_order = this.table_headers[ind].direction;
        await this.fetch_assessment_by_pagination(
          this.CurrentAssessment.page,
          this.CurrentAssessment.limit,
          this.assessment.id,
          false // Don't fetch all annotations, fetch only selected type
        );
      }
    },
    async open_user_details(
      user: AppAssessments.AssessmentUserObject,
      status: AppAssessments.AppAssessmentStatus | null
    ) {
      const response = await this.fetch_user_by_id(user.id);
      if (response) {
        this.user_object = response;
        this.on_boarding_invitation = true;
        this.status = status;
      }
    },
    update_status_filter(filter: string) {
      this.CurrentAssessment.status_filter = filter;
      this.process_assessment_listing(1);
    },
    check_status(assessment: AppAssessments.AnnotationAssessmentResults) {
      if (assessment.status)
        return [
          AppAssessments.AppAssessmentStatus.IN_PROGRESS,
          AppAssessments.AppAssessmentStatus.CANCELLED,
          AppAssessments.AppAssessmentStatus.FAILED
        ].includes(assessment.status);
      return false;
    },
    status_safe(
      status: AppAssessments.AppAssessmentStatus | null
    ): AppAssessments.AppAssessmentStatus {
      // prettier-ignore
      return (status ?? AppAssessments.AppAssessmentStatus.IN_PROGRESS) as AppAssessments.AppAssessmentStatus;
    },
    refresh_listing() {
      if (this.assessment?.id)
        this.fetch_assessment_by_pagination(
          this.CurrentAssessment.page,
          this.CurrentAssessment.limit,
          this.assessment?.id,
          false // Don't fetch all annotations, fetch only selected type
        );
    }
  }
});
